import { useState, type FormEvent } from 'react';
import { UserPlus } from 'lucide-react';
import { Async, EmptyState, PageHeader } from '../../components/ui/States';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useUser } from '../../auth/AuthContext';
import { canManage } from '../../auth/roles';
import { api, ApiError } from '../../lib/api';
import { errorMessage, useApi } from '../../hooks/useApi';
import { btnGhost, btnPrimary, cardCls, ConfirmModal, formatDate, inputCls, TempPasswordModal } from './shared';

interface Creator {
  id: string;
  displayName: string;
  email: string | null;
  status: 'pending' | 'active' | 'blocked';
  revenueSharePct: number;
  createdAt: string;
  hasLogin: boolean;
  lastLoginAt: string | null;
  titleCount: number;
}

const STATUS: Record<Creator['status'], { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  active: { label: 'Active', variant: 'success' },
  pending: { label: 'Pending', variant: 'warning' },
  blocked: { label: 'Blocked', variant: 'danger' },
};

type Pending = { kind: 'block' | 'unblock' | 'reset'; creator: Creator };

export function Creators() {
  const user = useUser();
  const manage = canManage(user.role);
  const state = useApi<{ creators: Creator[] }>('/admin/creators');

  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ password: string; who: string } | null>(null);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [share, setShare] = useState('');

  const closeAdd = () => {
    setAdding(false);
    setError(null);
    setEmail('');
    setName('');
    setShare('');
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: { email: string; displayName: string; revenueSharePct?: number } = { email: email.trim(), displayName: name.trim() };
      if (share.trim() !== '') body.revenueSharePct = Number(share);
      const r = await api<{ temporaryPassword: string; email: string }>('/admin/creators', { method: 'POST', body });
      closeAdd();
      setSecret({ password: r.temporaryPassword, who: `Login for ${r.email}` });
      state.reload();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'account_exists' ? 'An account with this email already exists.' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!pending) return;
    const { kind, creator } = pending;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'reset') {
        const r = await api<{ temporaryPassword: string }>(`/admin/creators/${creator.id}/reset-password`, { method: 'POST' });
        setSecret({ password: r.temporaryPassword, who: `${creator.hasLogin ? 'New password' : 'Login'} for ${creator.displayName}` });
      } else {
        await api(`/admin/creators/${creator.id}`, { method: 'PATCH', body: { status: kind === 'block' ? 'blocked' : 'active' } });
      }
      setPending(null);
      state.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const pendingText = () => {
    if (!pending) return { title: '', desc: '', label: '' };
    const n = pending.creator.displayName;
    if (pending.kind === 'block') return { title: `Block ${n}?`, desc: 'They will be signed out immediately and cannot sign in until you unblock them.', label: 'Block creator' };
    if (pending.kind === 'unblock') return { title: `Unblock ${n}?`, desc: 'They will be able to sign in again.', label: 'Unblock creator' };
    return pending.creator.hasLogin
      ? { title: `Reset password for ${n}?`, desc: 'A new temporary password will be created. Their old password stops working.', label: 'Reset password' }
      : { title: `Create a login for ${n}?`, desc: 'A temporary password will be created so they can sign in.', label: 'Create login' };
  };
  const pt = pendingText();

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Creators"
        subtitle={manage ? 'People who upload dramas to the platform. Add a creator, give them a login, or block them.' : 'People who upload dramas to the platform.'}
        actions={
          manage && (
            <button onClick={() => setAdding(true)} className={`${btnPrimary} flex items-center gap-1.5`}>
              <UserPlus className="w-3.5 h-3.5" /> Add creator
            </button>
          )
        }
      />
      <Async state={state}>
        {({ creators }) =>
          creators.length === 0 ? (
            <EmptyState
              title="No creators yet"
              message={manage ? 'Add your first creator to let them upload dramas.' : 'Creators will appear here once an admin adds them.'}
            />
          ) : (
            <div className={`${cardCls} overflow-x-auto`}>
              <table className="w-full text-left text-xs">
                <thead className="text-gray-400 border-b border-[#241b33]">
                  <tr>
                    <th className="px-4 py-3 font-medium">Creator</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Titles</th>
                    <th className="px-4 py-3 font-medium">Revenue share</th>
                    <th className="px-4 py-3 font-medium">Last signed in</th>
                    {manage && <th className="px-4 py-3 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#241b33]">
                  {creators.map((c) => (
                    <tr key={c.id}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-white">{c.displayName}</p>
                        <p className="text-gray-400">{c.email ?? 'No email'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS[c.status].variant}>{STATUS[c.status].label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-200">{c.titleCount}</td>
                      <td className="px-4 py-3 text-gray-200">{c.revenueSharePct}%</td>
                      <td className="px-4 py-3 text-gray-300">{c.hasLogin ? formatDate(c.lastLoginAt) : 'No login yet'}</td>
                      {manage && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              className={btnGhost}
                              disabled={!c.email}
                              title={!c.email ? 'This creator has no email address, so a login cannot be created.' : undefined}
                              onClick={() => {
                                setError(null);
                                setPending({ kind: 'reset', creator: c });
                              }}
                            >
                              {c.hasLogin ? 'Reset password' : 'Create login'}
                            </button>
                            <button
                              className={btnGhost}
                              onClick={() => {
                                setError(null);
                                setPending({ kind: c.status === 'blocked' ? 'unblock' : 'block', creator: c });
                              }}
                            >
                              {c.status === 'blocked' ? 'Unblock' : 'Block'}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Async>

      <Modal open={adding} onOpenChange={(o) => !o && !busy && closeAdd()} title="Add creator" description="They get a temporary password and choose their own when they first sign in." maxWidth="md">
        <form onSubmit={submitAdd} className="space-y-4">
          <div>
            <label htmlFor="c-name" className="block text-xs font-semibold text-gray-300 mb-1">Name</label>
            <input id="c-name" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="c-email" className="block text-xs font-semibold text-gray-300 mb-1">Email</label>
            <input id="c-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="c-share" className="block text-xs font-semibold text-gray-300 mb-1">Revenue share % (optional)</label>
            <input id="c-share" type="number" min={0} max={100} step="any" value={share} onChange={(e) => setShare(e.target.value)} className={inputCls} />
          </div>
          {error && <p className="text-xs text-rose-400" role="alert">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeAdd} disabled={busy} className={btnGhost}>Cancel</button>
            <button type="submit" disabled={busy} className={btnPrimary}>{busy ? 'Adding…' : 'Add creator'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        open={pending !== null}
        title={pt.title}
        description={pt.desc}
        confirmLabel={pt.label}
        danger={pending?.kind === 'block'}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />

      <TempPasswordModal password={secret?.password ?? null} who={secret?.who ?? ''} onClose={() => setSecret(null)} />
    </div>
  );
}
